import { Component, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { DrawerModule } from 'primeng/drawer';
import { TagModule } from 'primeng/tag';
import { RealtimeService, RealtimeNotification } from '../../../core/services/realtime.service';

@Component({
  selector: 'app-notification-center',
  imports: [CommonModule, DatePipe, TranslateModule, ButtonModule, BadgeModule, DrawerModule, TagModule],
  template: `
    <div class="notification-trigger">
      <p-button icon="pi pi-bell" [text]="true" [rounded]="true"
        [badge]="realtime.unreadCount() > 0 ? '' + realtime.unreadCount() : undefined"
        badgeSeverity="danger"
        (onClick)="visible = true" />
    </div>

    <p-drawer [(visible)]="visible" [header]="'notifications.title' | translate" position="right"
      [style]="{ width: '380px' }" appendTo="body">
      <div class="notif-header-actions">
        <p-button [label]="'notifications.markAllRead' | translate" [text]="true" size="small"
          icon="pi pi-check-circle" (onClick)="realtime.markAllRead()" />
        <p-button [label]="'notifications.clear' | translate" [text]="true" size="small"
          icon="pi pi-trash" severity="danger" (onClick)="realtime.clearAll()" />
      </div>

      <div class="notif-list">
        @for (n of realtime.notifications(); track n.id) {
          <div class="notif-item" [class.unread]="!n.read" (click)="realtime.markAsRead(n.id)">
            <div class="notif-icon" [class]="'severity-' + n.severity">
              <i [class]="getIcon(n.type)"></i>
            </div>
            <div class="notif-content">
              <div class="notif-title">{{ n.title }}</div>
              <div class="notif-message">{{ n.message }}</div>
              <div class="notif-time">{{ n.timestamp | date:'shortTime' }}</div>
            </div>
          </div>
        }

        @if (realtime.notifications().length === 0) {
          <div class="notif-empty">
            <i class="pi pi-bell-slash"></i>
            <p>{{ 'notifications.empty' | translate }}</p>
          </div>
        }
      </div>
    </p-drawer>
  `,
  styles: [`
    .notif-header-actions { display: flex; justify-content: space-between; margin-bottom: 0.75rem; }
    .notif-list { display: flex; flex-direction: column; gap: 0.25rem; }
    .notif-item {
      display: flex; gap: 0.75rem; padding: 0.75rem; border-radius: 8px; cursor: pointer;
      transition: background 0.15s;
      &:hover { background: var(--p-surface-50, #f8fafc); }
      &.unread { background: var(--p-highlight-background, #f0fdf4); }
    }
    .notif-icon {
      width: 36px; height: 36px; border-radius: 8px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      i { font-size: 1rem; color: #fff; }
      &.severity-info { background: #3b82f6; }
      &.severity-warn { background: #f59e0b; }
      &.severity-error { background: #ef4444; }
      &.severity-success { background: #10b981; }
    }
    .notif-content { flex: 1; min-width: 0; }
    .notif-title { font-weight: 600; font-size: 0.85rem; }
    .notif-message { font-size: 0.8rem; color: var(--p-text-muted-color); margin-top: 0.125rem;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .notif-time { font-size: 0.7rem; color: var(--p-text-muted-color); margin-top: 0.25rem; }
    .notif-empty {
      text-align: center; padding: 3rem 1rem; color: var(--p-text-muted-color);
      i { font-size: 2.5rem; } p { margin: 0.75rem 0 0; }
    }
  `],
})
export class NotificationCenter {
  realtime = inject(RealtimeService);
  visible = false;

  getIcon(type: string): string {
    const map: Record<string, string> = {
      GEOFENCE_ALERT: 'pi pi-map-marker',
      TRIP_UPDATE: 'pi pi-car',
      PROMO_REDEEM: 'pi pi-tag',
      COMMISSION: 'pi pi-dollar',
      SYSTEM: 'pi pi-cog',
    };
    return map[type] ?? 'pi pi-bell';
  }
}
